import { useNotification, useTranslation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { extractErrorDescription } from 'utils/errorUtils';
import {
  extractAppliedRuleMessagesFromSuccess,
  extractRuleWarningMessagesFromSuccess,
} from 'utils/ruleUtils';

interface UseMutationQueryParams<T> {
  mutationFn: (params?: any) => Promise<T | null>;
  successMessage: string;
  errorMessage: string;
  queryKey: string[];
  mutationKey?: string[];
  displayNotification?: boolean;
  invalidateQueryKeys?:
    | any[][]
    | ((data: T, variables: any | undefined) => any[][]);
}

const splitWarningMessage = (
  warningMessage: string
): { ruleLabel: string | null; details: string } => {
  const separator = ': ';
  const splitIndex = warningMessage.indexOf(separator);

  if (splitIndex <= 0) {
    return {
      ruleLabel: null,
      details: warningMessage,
    };
  }

  const ruleLabel = warningMessage.slice(0, splitIndex).trim();
  const details = warningMessage.slice(splitIndex + separator.length).trim();

  if (!ruleLabel || !details) {
    return {
      ruleLabel: null,
      details: warningMessage,
    };
  }

  return {
    ruleLabel,
    details,
  };
};

const formatWarningDescription = (
  warningMessages: string[],
  detailsTitle: string
): string => {
  const lines: string[] = [detailsTitle];

  warningMessages.forEach((warningMessage, index) => {
    const { ruleLabel, details } = splitWarningMessage(warningMessage);

    if (ruleLabel) {
      lines.push(`${index + 1}. ${ruleLabel}`);
      lines.push(`   ${details}`);
      return;
    }

    lines.push(`${index + 1}. ${details}`);
  });

  return lines.join('\n');
};

export const useMutationQuery = <T>({
  mutationFn,
  successMessage,
  errorMessage,
  queryKey,
  mutationKey,
  displayNotification = true,
  invalidateQueryKeys,
}: UseMutationQueryParams<T>): UseMutationResult<T, Error> => {
  const { open } = useNotification();
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const key = [...queryKey].filter((item) => item !== undefined);
  const errorNotificationTitle = translate(
    'common:notifications.errorTitle',
    'Error'
  );
  const successNotificationTitle = translate(
    'common:notifications.successTitle',
    'Success'
  );
  const warningNotificationTitle = translate(
    'common:notifications.warningTitle',
    'Warning'
  );
  const warningDetailsTitle = translate(
    'common:notifications.ruleWarnings.detailsTitle',
    'Warnings triggered:'
  );

  const warningCountLabel = (count: number): string =>
    translate(
      'common:notifications.ruleWarnings.count',
      { count },
      `${count} warning${count === 1 ? '' : 's'}`
    );

  return useMutation<T, Error>({
    mutationKey,
    mutationFn: (params?: any) => mutationFn(params),
    onError: (error) => {
      Sentry.captureException(error);
      queryClient.invalidateQueries({
        queryKey: key,
      });
      const description = extractErrorDescription(error);
      open?.({
        message: errorMessage,
        description,
        type: 'error',
        key: errorNotificationTitle,
      });
    },
    onSuccess: async (data, variables) => {
      const warningMessages = extractRuleWarningMessagesFromSuccess(data);
      const appliedMessages = extractAppliedRuleMessagesFromSuccess(data);
      const warningDescription =
        warningMessages.length > 0
          ? formatWarningDescription(warningMessages, warningDetailsTitle)
          : undefined;
      const appliedDescription =
        appliedMessages.length > 0
          ? formatWarningDescription(
              appliedMessages,
              translate(
                'common:notifications.ruleApplications.detailsTitle',
                'Applied rule actions:'
              )
            )
          : undefined;
      const combinedDescription = [appliedDescription, warningDescription]
        .filter(Boolean)
        .join('\n\n');

      if (displayNotification) {
        open?.({
          message: combinedDescription
            ? `${successMessage} (${warningCountLabel(
                warningMessages.length + appliedMessages.length
              )})`
            : successMessage,
          description: combinedDescription || undefined,
          type: combinedDescription ? 'progress' : 'success',
          key: combinedDescription
            ? warningNotificationTitle
            : successNotificationTitle,
        });
      }

      const invalidationKeys =
        typeof invalidateQueryKeys === 'function'
          ? invalidateQueryKeys(data, variables)
          : invalidateQueryKeys;

      if (invalidationKeys?.length) {
        await Promise.all(
          invalidationKeys.map((queryKey) =>
            queryClient.invalidateQueries({
              queryKey: queryKey.filter((item) => item !== undefined),
            })
          )
        );
      } else if (key) {
        await queryClient.invalidateQueries({
          queryKey: key,
        });
      }
    },
    retry: 0,
  });
};
